import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Search, BarChart3, List, X, Snowflake, Filter as FilterIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSearchParams } from 'react-router-dom';
import CompanyDialog from '../components/CompanyDialog';
import TypsaOrgUnitDialog from '../components/TypsaOrgUnitDialog';
import ExportMenu from '../components/ExportMenu';
import ClientTypeFilter from '../components/ClientTypeFilter';
import { matchesClientTypeFilter } from '../utils/clientType';
import { useUser } from '../context/UserContext';
import { useCompaniesData } from '../hooks/useCompaniesData';
import { TypsaSection } from '../components/companies/TypsaSection';
import { CompanySection } from '../components/companies/CompanySection';
import { CompaniesAnalytics } from '../components/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Analytics-driven URL params handled by this page
const ANALYTICS_FILTER_KEYS = [
  'has_open_opp', 'no_activity', 'cooling', 'no_contacts',
  'open_opps_stale', 'opps_incomplete', 'incomplete',
  'missing', 'activity_bucket', 'maturity', 'status', 'owner', 'companyId',
];
const CHIP_LABELS = {
  has_open_opp: 'Con pipeline abierto',
  no_activity: 'Sin actividad',
  cooling: 'Enfriándose',
  no_contacts: 'Sin contactos',
  open_opps_stale: 'Opps sin actividad 30d',
  opps_incomplete: 'Opps sin importe/fecha',
  incomplete: 'Fichas incompletas',
};
const MATURITY_LABELS = {
  with_open_pipeline: 'Con pipeline abierto',
  only_closed: 'Solo historial cerrado',
  only_closed_ever_won: 'Cerradas · alguna vez ganada',
  only_closed_never_won: 'Cerradas · nunca ganada',
  no_opps_ever: 'Sin ninguna oportunidad',
};

const Companies = () => {
  const { isEditor, isAdmin, currentUser } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  // View mode from URL (?view=analytics) or default 'list'
  const initialViewMode = searchParams.get('view') === 'analytics' ? 'analytics' : 'list';
  const [viewMode, setViewMode] = useState(initialViewMode);
  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    if (viewMode === 'analytics') {
      p.set('view', 'analytics');
    } else {
      p.delete('view');
    }
    setSearchParams(p, { replace: true });
  }, [viewMode]);

  // Client type filter state
  const [clientTypeFilter, setClientTypeFilter] = useState(['typsa_gg', 'typsa_colab', 'privada', 'publica']);

  // ── Analytics-driven filters via URL params ────────────────────────────────
  const activeFilterKeys = ANALYTICS_FILTER_KEYS.filter(k => searchParams.get(k));
  const hasAnalyticsFilter = activeFilterKeys.some(k => !['status','owner','companyId'].includes(k)) || !!searchParams.get('maturity');

  const scopeParam = searchParams.get('scope') || 'external';
  const [filterIds, setFilterIds] = useState(null); // null=loading, array=loaded

  useEffect(() => {
    if (!hasAnalyticsFilter) { setFilterIds(null); return; }
    const load = async () => {
      try {
        const token = localStorage.getItem('crm_token');
        const params = new URLSearchParams();
        params.append('scope', scopeParam);
        params.append('period', 'all');
        const url = `${API}/analytics/companies/v2?${params.toString()}`;
        const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const fid = data?.filter_ids || {};

        // Compute intersection of active filters
        const sets = [];
        if (searchParams.get('has_open_opp') === 'true') sets.push(new Set(fid.has_open_opp || []));
        if (searchParams.get('no_activity') === 'true') sets.push(new Set(fid.no_activity || []));
        if (searchParams.get('cooling') === 'true') sets.push(new Set(fid.cooling || []));
        if (searchParams.get('no_contacts') === 'true') sets.push(new Set(fid.no_contacts || []));
        if (searchParams.get('open_opps_stale') === 'true') sets.push(new Set(fid.open_opps_stale || []));
        if (searchParams.get('opps_incomplete') === 'true') sets.push(new Set(fid.opps_incomplete || []));
        if (searchParams.get('incomplete') === 'true') sets.push(new Set(fid.incomplete || []));
        const missing = searchParams.get('missing');
        if (missing) sets.push(new Set(fid.missing?.[missing] || []));
        const bucket = searchParams.get('activity_bucket');
        if (bucket) {
          const URL_TO_KEY = { '0-30':'0_30','31-90':'31_90','91-180':'91_180','181-365':'181_365','gt-365':'gt_365','nunca':'nunca' };
          const key = URL_TO_KEY[bucket];
          sets.push(new Set(fid.activity_bucket?.[key] || []));
        }
        const maturity = searchParams.get('maturity');
        if (maturity) {
          sets.push(new Set(fid.maturity?.[maturity] || []));
        }
        // Intersect
        if (sets.length === 0) { setFilterIds([]); return; }
        let inter = sets[0];
        for (let i = 1; i < sets.length; i++) {
          inter = new Set([...inter].filter(x => sets[i].has(x)));
        }
        setFilterIds([...inter]);
      } catch (e) {
        console.error('filter load', e);
        setFilterIds([]);
      }
    };
    load();
  }, [searchParams, scopeParam, hasAnalyticsFilter]);

  const clearAllFilters = () => {
    const p = new URLSearchParams(searchParams);
    ANALYTICS_FILTER_KEYS.forEach(k => p.delete(k));
    p.delete('scope');
    setSearchParams(p, { replace: true });
  };
  const clearFilter = (key) => {
    const p = new URLSearchParams(searchParams);
    p.delete(key);
    setSearchParams(p, { replace: true });
  };

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPreselectedType, setDialogPreselectedType] = useState(null);
  const [typsaDialogOpen, setTypsaDialogOpen] = useState(false);
  const [editingTypsaUnit, setEditingTypsaUnit] = useState(null);

  // Hook con toda la lógica de datos
  const {
    loading,
    search,
    setSearch,
    typsaGroupByOwner,
    setTypsaGroupByOwner,
    typsaCategories,
    filteredTypsaCount,
    totalNonTypsaCompanies,
    totalTypsaUnits,
    publicaStatusFilter,
    setPublicaStatusFilter,
    publicaRolFilter,
    setPublicaRolFilter,
    publicaSortBy,
    setPublicaSortBy,
    privadaStatusFilter,
    setPrivadaStatusFilter,
    privadaRolFilter,
    setPrivadaRolFilter,
    privadaSortBy,
    setPrivadaSortBy,
    expandedTypes,
    expandedGroups,
    expandedOwnerGroups,
    toggleType,
    toggleGroup,
    toggleOwnerGroup,
    companiesByType,
    fetchCompanies,
    fetchTypsaOrgUnits,
    handleDeleteTypsaUnit,
  } = useCompaniesData(currentUser);

  // TYPSA dialog handlers
  const handleEditTypsaUnit = (unit) => {
    setEditingTypsaUnit(unit);
    setTypsaDialogOpen(true);
  };
  const handleNewTypsaUnit = () => {
    setEditingTypsaUnit(null);
    setTypsaDialogOpen(true);
  };
  const handleNewCompany = (type) => {
    setDialogPreselectedType(type);
    setDialogOpen(true);
  };
  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) setDialogPreselectedType(null);
  };
  const toggleClientTypeFilter = (type) => {
    setClientTypeFilter(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // URL-based status/owner filter (from analytics navigation)
  const urlStatus = searchParams.get('status');
  const urlOwner = searchParams.get('owner');
  const urlCompanyId = searchParams.get('companyId');

  const privadaGroup = companiesByType.find(t => t.key === 'privada');
  const publicaGroup = companiesByType.find(t => t.key === 'publica');

  const filterIdSet = useMemo(() => new Set(filterIds || []), [filterIds]);
  const applyAllFilters = (group) => {
    if (!group) return group;
    let comps = group.companies;
    if (hasAnalyticsFilter && filterIds !== null) {
      comps = comps.filter(c => filterIdSet.has(c.id));
    }
    if (urlStatus) {
      if (urlStatus === '__unassigned__') {
        comps = comps.filter(c => !(c.status && c.status.trim()));
      } else {
        comps = comps.filter(c => c.status === urlStatus);
      }
    }
    if (urlOwner) {
      if (urlOwner === '__unassigned__') {
        comps = comps.filter(c => !(c.owner && c.owner.trim()));
      } else {
        comps = comps.filter(c => c.owner === urlOwner);
      }
    }
    if (urlCompanyId) {
      comps = comps.filter(c => c.id === urlCompanyId);
    }
    return {
      ...group,
      companies: comps,
      groups: (group.groups || []).map(g => ({
        ...g,
        companies: (g.companies || []).filter(c => comps.some(cc => cc.id === c.id)),
      })).filter(g => g.companies.length > 0),
    };
  };
  const filteredPrivadaGroup = applyAllFilters(privadaGroup);
  const filteredPublicaGroup = applyAllFilters(publicaGroup);

  const anyExtraFilter = hasAnalyticsFilter || !!urlStatus || !!urlOwner || !!urlCompanyId;
  const filteredCount = anyExtraFilter
    ? ((filteredPrivadaGroup?.companies?.length || 0) + (filteredPublicaGroup?.companies?.length || 0))
    : 0;

  // Auto-expand relevant groups when a filter is applied
  useEffect(() => {
    if (!anyExtraFilter) return;
    if ((filteredPrivadaGroup?.companies?.length || 0) > 0 && !expandedTypes.privada) toggleType('privada');
    if ((filteredPublicaGroup?.companies?.length || 0) > 0 && !expandedTypes.publica) toggleType('publica');
  }, [anyExtraFilter, filterIds, urlStatus, urlOwner, urlCompanyId]);

  // TYPSA sub-classification (GG vs Colab.) lives on Opportunity, so both
  // chips act as an OR bucket for Empresas — either one keeps TYPSA units visible.
  const showTypsa = matchesClientTypeFilter('typsa', clientTypeFilter) && !anyExtraFilter;
  const showPrivada = clientTypeFilter.includes('privada');
  const showPublica = clientTypeFilter.includes('publica');

  return (
    <div className="p-4 md:p-8" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="companies-title">Empresas</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {viewMode === 'analytics' ? (
              <span className="opacity-60 text-xs">Ver contadores en la analítica</span>
            ) : anyExtraFilter ? (
              <span data-testid="companies-filtered-count">
                <strong>{filterIds == null && hasAnalyticsFilter ? '…' : filteredCount}</strong> de {totalNonTypsaCompanies + totalTypsaUnits} empresas <span className="text-xs">(filtrado)</span>
              </span>
            ) : (
              <>
                {totalNonTypsaCompanies + totalTypsaUnits} empresas
                {clientTypeFilter.length < 4 && <span className="text-xs ml-1">(filtrado)</span>}
              </>
            )}
          </p>
          {anyExtraFilter && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {activeFilterKeys.filter(k => searchParams.get(k) === 'true').map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => clearFilter(k)}
                  className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200"
                  data-testid={`chip-${k}`}
                >
                  {k === 'cooling' && <Snowflake className="w-3 h-3" />}
                  {k !== 'cooling' && <FilterIcon className="w-3 h-3" />}
                  {CHIP_LABELS[k] || k} <X className="w-3 h-3" />
                </button>
              ))}
              {searchParams.get('missing') && (
                <button type="button" onClick={() => clearFilter('missing')} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200" data-testid="chip-missing">
                  Sin {searchParams.get('missing')} <X className="w-3 h-3" />
                </button>
              )}
              {searchParams.get('activity_bucket') && (
                <button type="button" onClick={() => clearFilter('activity_bucket')} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200" data-testid="chip-activity-bucket">
                  Actividad: {searchParams.get('activity_bucket')} <X className="w-3 h-3" />
                </button>
              )}
              {searchParams.get('maturity') && (
                <button type="button" onClick={() => clearFilter('maturity')} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200" data-testid="chip-maturity">
                  {MATURITY_LABELS[searchParams.get('maturity')] || searchParams.get('maturity')} <X className="w-3 h-3" />
                </button>
              )}
              {urlStatus && (
                <button type="button" onClick={() => clearFilter('status')} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200" data-testid="chip-status">
                  Estado: {urlStatus} <X className="w-3 h-3" />
                </button>
              )}
              {urlOwner && (
                <button type="button" onClick={() => clearFilter('owner')} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200" data-testid="chip-owner">
                  Responsable: {urlOwner === '__unassigned__' ? 'Sin asignar' : urlOwner} <X className="w-3 h-3" />
                </button>
              )}
              {urlCompanyId && (
                <button type="button" onClick={() => clearFilter('companyId')} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 hover:bg-blue-200" data-testid="chip-company">
                  Empresa concreta <X className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs text-muted-foreground underline hover:text-foreground ml-1"
                data-testid="clear-all-filters"
              >
                Limpiar todos
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border p-1 bg-muted/30">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
              data-testid="companies-list-tab"
            >
              <List className="w-4 h-4 mr-2" />
              Lista
            </Button>
            <Button
              variant={viewMode === 'analytics' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('analytics')}
              className="h-8 px-3"
              data-testid="companies-analytics-tab"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analíticas
            </Button>
          </div>
          <ClientTypeFilter selectedTypes={clientTypeFilter} onToggle={toggleClientTypeFilter} />
          <ExportMenu endpoint="companies" filters={{ search }} />
        </div>
      </div>

      {/* Content */}
      {viewMode === 'analytics' ? (
        <CompaniesAnalytics clientTypeFilter={clientTypeFilter} />
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Cargando...</div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar empresas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-companies-input"
              />
            </div>
          </div>

          <div className="space-y-4">
            {showTypsa && (
              <TypsaSection
                isExpanded={expandedTypes.typsa}
                onToggle={toggleType}
                filteredTypsaCount={filteredTypsaCount}
                typsaGroupByOwner={typsaGroupByOwner}
                setTypsaGroupByOwner={setTypsaGroupByOwner}
                typsaCategories={typsaCategories}
                expandedGroups={expandedGroups}
                expandedOwnerGroups={expandedOwnerGroups}
                onToggleGroup={toggleGroup}
                onToggleOwnerGroup={toggleOwnerGroup}
                onEditUnit={handleEditTypsaUnit}
                onDeleteUnit={handleDeleteTypsaUnit}
                onNewUnit={handleNewTypsaUnit}
                onUpdated={fetchTypsaOrgUnits}
                isEditor={isEditor}
                isAdmin={isAdmin}
              />
            )}
            {showPrivada && (
              <CompanySection
                typeKey="privada"
                typeGroup={filteredPrivadaGroup}
                isExpanded={expandedTypes.privada}
                onToggle={toggleType}
                sortBy={privadaSortBy}
                setSortBy={setPrivadaSortBy}
                statusFilter={privadaStatusFilter}
                setStatusFilter={setPrivadaStatusFilter}
                rolFilter={privadaRolFilter}
                setRolFilter={setPrivadaRolFilter}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                onNewCompany={() => handleNewCompany('Privada')}
                onUpdated={fetchCompanies}
                isEditor={isEditor}
              />
            )}
            {showPublica && (
              <CompanySection
                typeKey="publica"
                typeGroup={filteredPublicaGroup}
                isExpanded={expandedTypes.publica}
                onToggle={toggleType}
                sortBy={publicaSortBy}
                setSortBy={setPublicaSortBy}
                statusFilter={publicaStatusFilter}
                setStatusFilter={setPublicaStatusFilter}
                rolFilter={publicaRolFilter}
                setRolFilter={setPublicaRolFilter}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                onNewCompany={() => handleNewCompany('Pública')}
                onUpdated={fetchCompanies}
                isEditor={isEditor}
              />
            )}
          </div>
        </>
      )}

      {/* Dialogs */}
      <CompanyDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onSuccess={fetchCompanies}
        preselectedType={dialogPreselectedType}
      />
      <TypsaOrgUnitDialog
        open={typsaDialogOpen}
        onOpenChange={setTypsaDialogOpen}
        unit={editingTypsaUnit}
        onSuccess={fetchTypsaOrgUnits}
      />
    </div>
  );
};

export default Companies;
